import { Directive, EmbeddedViewRef, Injector, Input, OnChanges, SimpleChanges, TemplateRef, ViewContainerRef } from '@angular/core';

@Directive({
    selector: '[msGameDynamicTemplateOutlet]',
    standalone: false
})
export class GameDynamicTemplateOutletDirective<C extends object = {}> implements OnChanges {
  private _viewRef: EmbeddedViewRef<C> | null = null;
  
  /**
     * A context object to attach to the {@link EmbeddedViewRef}. This should be an
     * object, the object's keys will be available for binding by the local template `let`
     * declarations.
     * Using the key `$implicit` in the context object will set its value as default.
     */
  @Input() public ngTemplateOutletContext: C | null = null;
  
  /**
     * A string defining the template reference and optionally the context object for the template.
     */
  @Input() public ngTemplateOutlet: TemplateRef<C> | null = null;
  
  /** Injector to be used within the embedded view. */
  @Input() public ngTemplateOutletInjector: Injector | null = null;
  
  constructor(private _viewContainerRef: ViewContainerRef) {}
  
  ngOnChanges(changes: SimpleChanges) {
    if (this._shouldRecreateView(changes)) {
      const viewContainerRef = this._viewContainerRef;
  
      if (this._viewRef) {
        viewContainerRef.remove(viewContainerRef.indexOf(this._viewRef));
      }
  
      // If there is no outlet, clear the destroyed view ref.
      if (!this.ngTemplateOutlet) {
        this._viewRef = null;
        return;
      }
  
      // Create a context forward `Proxy` that will always bind to the user-specified context,
      // without having to destroy and re-create views whenever the context changes.
      const viewContext = this._createContextForwardProxy();
      this._viewRef = viewContainerRef.createEmbeddedView(this.ngTemplateOutlet, viewContext, {
        injector: this.ngTemplateOutletInjector ?? undefined,
      });
    }
  }
  
  /**
     * We need to re-create existing embedded view if either is true:
     * - the outlet changed.
     * - the injector changed.
     */
  private _shouldRecreateView(changes: SimpleChanges): boolean {
    return !!changes.ngTemplateOutlet || !!changes.ngTemplateOutletInjector;
  }
  
  /**
     * For a given outlet instance, we create a proxy object that delegates
     * to the user-specified context. This allows changing, or swapping out
     * the context object completely without having to destroy/re-create the view.
     */
  private _createContextForwardProxy(): C {
    return <C> new Proxy(
      {},
      {
        set: (_target, prop, newValue) => {
          if (!this.ngTemplateOutletContext) {
            return false;
          }
          return Reflect.set(this.ngTemplateOutletContext, prop, newValue);
        },
        get: (_target, prop, receiver) => {
          if (!this.ngTemplateOutletContext) {
            return undefined;
          }
          return Reflect.get(this.ngTemplateOutletContext, prop, receiver);
        },
      },
    );
  }
}